import { CalcInputError, assertCents, roundCents, sumCents, type Cents } from "./money.js";
import type { CalcLineItem, CalcPeriod, CascadeResult } from "./types.js";
import { deductContraRevenueCents, selectedRevenueLines } from "./revenue.js";

import { PL_GROUPS, assertLineMapping } from "./line-mapping.js";

const CONTRA_REVENUE = new Set<string>(PL_GROUPS.contraRevenue);
const COGS = new Set<string>(PL_GROUPS.cogs);
const OTHER_INCOME = new Set<string>(PL_GROUPS.otherIncome);
const OTHER_EXPENSES = new Set<string>(PL_GROUPS.otherExpenses);
const DEPRECIATION_AMORTISATION = new Set<string>(PL_GROUPS.depreciationAmortisation);
const INTEREST_EXPENSE = new Set<string>(PL_GROUPS.interest);
const TAX = new Set<string>(PL_GROUPS.tax);

function annualisationFactor(period: CalcPeriod): number | null {
  if (period.periodType !== "ytd" || period.monthsInPeriod >= 12) return null;
  if (period.monthsInPeriod <= 0) throw new CalcInputError("monthsInPeriod must be positive");
  return 12 / period.monthsInPeriod;
}

function scaledAmount(line: CalcLineItem, factor: number | null, heldFlat: ReadonlySet<string>): Cents {
  assertCents(line.amountCents);
  if (factor === null || heldFlat.has(line.lineItem)) return line.amountCents;
  return roundCents(line.amountCents * factor);
}

function sumLines(
  lines: readonly CalcLineItem[],
  predicate: (lineItem: string) => boolean,
  factor: number | null,
  heldFlat: ReadonlySet<string>,
): Cents {
  return sumCents(lines.filter((line) => predicate(line.lineItem)).map((line) => scaledAmount(line, factor, heldFlat)));
}

function isOperatingExpense(lineItem: string): boolean {
  return (PL_GROUPS.operatingExpenses as readonly string[]).includes(lineItem);
}

export function cascade(period: CalcPeriod): CascadeResult {
  for (const line of period.profitLoss) assertLineMapping(line.lineItem, "profitLoss");
  const factor = annualisationFactor(period);
  const heldFlat = new Set(period.heldFlatLineItems ?? []);

  // Select on recorded rows once; scaling can change whether gross sums to zero.
  const revenueLines = selectedRevenueLines(period.profitLoss).map(
    line => ({ ...line, amountCents: scaledAmount(line, factor, heldFlat) }),
  );
  const contraRevenueLines = period.profitLoss.filter(line => CONTRA_REVENUE.has(line.lineItem)).map(
    line => ({ ...line, amountCents: scaledAmount(line, factor, heldFlat) }),
  );
  const operatingIncomeCents = sumCents(revenueLines.map(line => line.amountCents));
  const contraRevenueCents = sumCents(contraRevenueLines.map(line => line.amountCents));
  const cogsCents = sumLines(period.profitLoss, (lineItem) => COGS.has(lineItem), factor, heldFlat);
  const grossProfitCents = sumCents([deductContraRevenueCents(operatingIncomeCents, contraRevenueCents), -cogsCents]);
  const otherIncomeCents = sumLines(period.profitLoss, (lineItem) => OTHER_INCOME.has(lineItem), factor, heldFlat);
  const otherExpensesCents = sumLines(period.profitLoss, (lineItem) => OTHER_EXPENSES.has(lineItem), factor, heldFlat);
  const netOtherIncomeCents =
    period.profitLoss.some(line => OTHER_INCOME.has(line.lineItem)) && period.profitLoss.some(line => OTHER_EXPENSES.has(line.lineItem))
      ? sumCents([otherIncomeCents, -otherExpensesCents]) : null;
  const operatingExpensesCents = sumLines(period.profitLoss, isOperatingExpense, factor, heldFlat);
  const ebitdaCents = sumCents([grossProfitCents, otherIncomeCents, -otherExpensesCents, -operatingExpensesCents]);
  const depreciationAmortisationCents = sumLines(
    period.profitLoss,
    (lineItem) => DEPRECIATION_AMORTISATION.has(lineItem),
    null,
    heldFlat,
  );
  const ebitCents = sumCents([ebitdaCents, -depreciationAmortisationCents]);
  const interestExpenseCents = sumLines(period.profitLoss, (lineItem) => INTEREST_EXPENSE.has(lineItem), null, heldFlat);
  const npbtCents = sumCents([ebitCents, -interestExpenseCents]);
  const taxCents = sumLines(period.profitLoss, (lineItem) => TAX.has(lineItem), null, heldFlat);

  return {
    operatingIncomeCents,
    contraRevenueCents,
    cogsCents,
    grossProfitCents,
    otherIncomeCents,
    otherExpensesCents,
    netOtherIncomeCents,
    operatingExpensesCents,
    ebitdaCents,
    depreciationAmortisationCents,
    ebitCents,
    interestExpenseCents,
    npbtCents,
    taxCents,
    npatCents: sumCents([npbtCents, -taxCents]),
    annualised: factor !== null,
    annualisationFactor: factor,
  };
}
